import { appState } from "@/app/appState";
import { ChatDocument, chatState, ChatTag } from "@/app/chatState";
import { EntryItem, useRefState } from "@/components/UI/tools";
import { useEffect, useMemo, useState } from "react";
import { shortenLabel } from "../chatObjectEditorWindow";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { adminUpdateDocument, adminDeleteDocument } from "@/api/sdk.gen";
import { AdminUpdateDocumentData, AdminDeleteDocumentData } from "@/api/types.gen";
import { Request } from "@/components/CRUD";
import { Org, User } from "@/components/UI/svgs";
import { tag_list_item } from "@/app/typing/tags";
import { PdfViewer } from "@/components/PdfViewer";
import { TextViewer } from "@/components/TextViewer";
import { ArtifactEditor } from "./artifactEditor";

/**
 * A document is a source, not a work product. The viewer takes the whole panel
 * and the editable surface is deliberately tiny: a document has no content to
 * write into and no revisions, so only its name and owning tag can change.
 */
export const DocumentEditor = observer(() => {
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [edit, setEdit] = useState(false);
    const [tagSearch, setTagSearch] = useState("");
    const [tagPageNumber, setTagPageNumber] = useState(0);

    const [variableName, setVariableName] = useRefState<string>("");
    const [tagId, setTagId] = useRefState<number | undefined>(undefined);

    const activeKey = chatState.selectedObjectKey;

    const doc = useMemo(() => {
        return runInAction(() => {
            if (!activeKey || activeKey.type !== "document") return undefined;
            const obj = chatState.chatObjects.get(activeKey) as ChatDocument | undefined;
            if (!obj || !obj.document) {
                chatState.getObjectByKey({ type: "document", id: activeKey.id });
                return undefined;
            }
            return obj.document;
        });
    }, [activeKey?.id, activeKey?.type, chatState.chatObjects.get(activeKey)]);

    useEffect(() => {
        setVariableName(doc?.variable_name ?? "");
        setTagId(doc?.tag_id);
        setEdit(false);
        setRefresh((prev) => prev + 1);
    }, [doc?.id, doc?.variable_name, doc?.tag_id]);

    const update = Request<AdminUpdateDocumentData, any>(
        adminUpdateDocument,
        () => ({
            body: {
                id: doc!.id,
                variable_name: variableName.current,
                tag_id: tagId.current,
            },
        }),
        setLoading,
        () => {
            setEdit(false);
            if (activeKey) chatState.getObjectByKey(activeKey);
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? "",
            }),
        () => !!doc && variableName.current.length > 0,
    );

    const remove = Request<AdminDeleteDocumentData, any>(
        adminDeleteDocument,
        () => ({ body: { document_id: doc!.id } }),
        setDeleting,
        () => {
            if (activeKey) chatState.forgetDeletedObjects([activeKey]);
            chatState.removeSelectedObjectKey();
        },
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? "",
            }),
        () => !!doc,
    );

    // A note picked under this document opens in its own editor, the same hand-off
    // an event makes to the note the user selected.
    const childIndex = (chatState.chatObjects.get(activeKey) as any)?.child_index;
    const treeNodes = activeKey ? chatState.chatObjects.entries([activeKey as any]) : [];
    const treeNode = treeNodes.length > 0 ? treeNodes[0] : undefined;

    if (childIndex !== undefined && treeNode?.children && treeNode.children.length > childIndex) {
        const childKey = treeNode.children[childIndex].key;
        switch (childKey.type) {
            case "artifact":
                // Remount on a different child so its temp edit state cannot leak across.
                return <ArtifactEditor key={`${childKey.type}-${childKey.id}`} objectKey={childKey} />;
        }
    }

    if (!doc) return null;

    const dateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
    const isPdf = doc.content_type === "application/pdf";
    const isText = doc.content_type === "text/plain";
    const hasViewer = isPdf || isText;

    return (
        <div className="flex flex-col h-full min-h-0 w-full">
            {isPdf && (
                <div className="flex-1 min-h-0 overflow-hidden">
                    <PdfViewer fill documentId={doc.id} filename={doc.original_filename} />
                </div>
            )}
            {isText && (
                <div className="flex-1 min-h-0 overflow-hidden">
                    <TextViewer fill documentId={doc.id} filename={doc.original_filename} />
                </div>
            )}
            <div className={hasViewer ? "shrink-0" : "flex-1 min-h-0"}>
                <EntryItem
                    unsavedChanges={edit}
                    lock={false}
                    setEdit={setEdit}
                    edit={edit}
                    name={appState.t("objects.list.document")}
                    created_at={new Date(doc.created_at ?? "").toLocaleDateString(undefined, dateOptions)}
                    loading={loading}
                    deleting={deleting}
                    readonly={
                        ![4, 5, 6, 7].includes(appState.user?.active_organisation.permission_type ?? 0) ||
                        ![4, 5, 6, 7].includes(doc.permission_type ?? 0)
                    }
                    chatEditorWindowClose={() => chatState.removeSelectedObjectKey()}
                    updateEntry={update}
                    deleteEntry={remove}
                    refreshCount={refresh}
                    idParams={{
                        singular: { get: doc.id, set: () => {} },
                        plural: { get: [], set: () => {} },
                    }}
                    nameColumn={{
                        type: "text",
                        value: variableName.current,
                        onChange: setVariableName,
                        validator: (val: string) => !/["'${}`,]/.test(val),
                        label: appState.t("objects.variableName"),
                    }}
                    stateColumns={[
                        {
                            label: "#",
                            type: "single",
                            onChange: (value: number | undefined) => setTagId(value),
                            search: { get: tagSearch, set: setTagSearch },
                            pageNumber: { get: tagPageNumber, set: setTagPageNumber },
                            value: tagId.current,
                            items: [
                                ...(appState.tags ?? []),
                                ...chatState.chatObjects
                                    .entries()
                                    .filter(
                                        (x) =>
                                            x.value.type === "tag" &&
                                            (x.value as ChatTag).tag &&
                                            !(appState.tags ?? []).find((t) => t.id === x.value.id),
                                    )
                                    .map((x) => (x.value as ChatTag).tag as any as tag_list_item),
                            ]
                                .filter((x) => [4, 5, 6, 7].includes(x.effective_permission_type))
                                .map((x) => ({
                                    id: x.id,
                                    name: x.name,
                                    children: (
                                        <div className="flex flex-row gap-[0.5rem] items-center">
                                            <>{shortenLabel(x.name)}</>
                                            {x.scope == "me" ? <User size={16} /> : <Org size={16} />}
                                        </div>
                                    ),
                                })),
                            validator: (val: number | undefined) => val !== undefined,
                        },
                    ]}
                />
            </div>
        </div>
    );
});
